import { useState, useEffect } from "react"
import { Search, AlertCircle, Eye, Lock, Unlock } from "lucide-react"
import { Button } from "../components/ui/button"
import { API_ENDPOINTS } from "../config/api"
import { fetchWithAuth } from "../utils/api"
import { useLanguage } from "../i18n/LanguageContext"

const GOVERNORATES = [
  "Cairo",
  "Giza",
  "Asyut",
  "Red Sea",
  "South Sinai",
  "Alexandria"
]

function UserProfilePhoto({ url, label, detail = false }) {
  const { t } = useLanguage()
  const [failed, setFailed] = useState(false)
  const imageClass = detail ? "user-detail-photo" : "user-photo-thumbnail"

  if (!url || failed) {
    return <div className={`${imageClass} user-photo-placeholder`}>{t("common.noPhoto")}</div>
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="user-photo-link"
      title={t("common.openImage", { label })}
    >
      <img
        src={url}
        alt={label}
        className={imageClass}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </a>
  )
}

export default function UserManagement() {
  const { t, translateStatus, translateGovernorate, translateApiMessage, formatNumber, formatCurrency } = useLanguage()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    fetchUsers()
    // Pagination and status are the complete inputs; manual search invokes the same loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter])

  const fetchUsers = async () => {
    try {
      let url = `${API_ENDPOINTS.USERS}?page=${page}&per_page=10`

      if (statusFilter) {
        url += `&status=${statusFilter}`
      }
      if (searchTerm) {
        url += `&search=${searchTerm}`
      }

      const res = await fetchWithAuth(url)

      if (res.ok) {
        const data = await res.json()
        setUsers(data.users)
        setTotalPages(data.pages)
      } else {
        setError(t("errors.fetchUsers"))
      }
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.fetchUsers"))
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1)
    fetchUsers()
  }

  const handleStatusChange = async (userId, newStatus) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.USER_STATUS(userId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      })

      if (res.ok) {
        fetchUsers()
        setShowModal(false)
      } else {
        setError(t("errors.updateUserStatus"))
      }
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.updateUserStatus"))
    }
  }

  const handleGovernorateChange = async (userId, governorate) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.USER_GOVERNORATE(userId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ governorate })
      })

      if (res.ok) {
        setUsers((currentUsers) =>
          currentUsers.map((user) =>
            user.user_id === userId ? { ...user, governorate } : user
          )
        )
        setSelectedUser((currentUser) =>
          currentUser && currentUser.user_id === userId
            ? { ...currentUser, governorate }
            : currentUser
        )
      } else {
        const data = await res.json().catch(() => ({}))
        setError(translateApiMessage(data.message, "errors.updateGovernorate"))
      }
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.updateGovernorate"))
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800"
      case "Deactivated":
        return "bg-orange-100 text-orange-800"
      case "Blocked":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="management-title font-bold text-gray-800">{t("users.title")}</h1>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSearch} className="management-filter-form flex gap-3">
          <div className="flex-1 relative">
            <Search className="search-icon-start absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t("users.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-with-icon w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            className="management-filter-select px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="">{t("common.allStatuses")}</option>
            <option value="Active">{t("status.active")}</option>
            <option value="Deactivated">{t("status.deactivated")}</option>
            <option value="Blocked">{t("status.blocked")}</option>
          </select>
          <Button type="submit" className="management-filter-submit bg-amber-600 hover:bg-amber-700">
            {t("common.search")}
          </Button>
        </form>
      </div>

      {/* Users Table */}
      <div className="data-table-container bg-white rounded-lg shadow">
        <table className="data-table user-table w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.name")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.email")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.phone")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("users.registrationLocation")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("users.userPhoto")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("users.governorate")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("users.bookings")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("users.totalSpent")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.status")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.length > 0 ? (
              users.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <span className="bidi-isolate">{user.email}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <span className="bidi-isolate">{user.phone_number}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.registration_location || t("common.notAvailable")}
                  </td>
                  <td className="px-6 py-4">
                    <UserProfilePhoto
                      url={user.profile_image_url}
                      label={t("users.profilePhotoAlt", { name: user.name })}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.governorate ? translateGovernorate(user.governorate) : t("common.notSpecified")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatNumber(user.statistics?.total_bookings || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatCurrency(user.statistics?.total_spent || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                        user.status
                      )}`}>
                      {translateStatus(user.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => {
                        setSelectedUser(user)
                        setShowModal(true)
                      }}
                      className="text-amber-600 hover:text-amber-800 font-medium">
                      <Eye className="w-4 h-4 inline" /> {t("common.view")}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                  {t("users.noUsers")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {t("common.pageOf", { page: formatNumber(page), total: formatNumber(totalPages) })}
        </p>
        <div className="flex gap-2">
          <Button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 disabled:opacity-50">
            {t("common.previous")}
          </Button>
          <Button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 disabled:opacity-50">
            {t("common.next")}
          </Button>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center modal-backdrop z-50">
          <div className="responsive-modal bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{selectedUser.name}</h2>

              <section className="user-detail-photo-section mb-6">
                <h3 className="user-photo-title">{t("users.profilePicture")}</h3>
                <UserProfilePhoto
                  url={selectedUser.profile_image_url}
                  label={t("users.profilePhotoAlt", { name: selectedUser.name })}
                  detail
                />
              </section>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">{t("common.email")}</p>
                  <p className="font-medium text-gray-800"><span className="bidi-isolate">{selectedUser.email}</span></p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("common.phone")}</p>
                  <p className="font-medium text-gray-800"><span className="bidi-isolate">{selectedUser.phone_number}</span></p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">{t("users.approximateRegistrationLocation")}</p>
                  <p className="font-medium text-gray-800">{selectedUser.registration_location || t("common.notAvailable")}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("users.totalBookings")}</p>
                  <p className="font-medium text-gray-800">
                    {formatNumber(selectedUser.statistics?.total_bookings || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("users.totalSpent")}</p>
                  <p className="font-medium text-gray-800">
                    {formatCurrency(selectedUser.statistics?.total_spent || 0)}
                  </p>
                </div>
                <div className="col-span-2">
                  <label htmlFor="user-governorate" className="text-sm text-gray-600">
                    {t("users.governorate")}
                  </label>
                  <select
                    id="user-governorate"
                    value={selectedUser.governorate || ""}
                    onChange={(e) => handleGovernorateChange(selectedUser.user_id, e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500">
                    <option value="">{t("common.notSpecified")}</option>
                    {GOVERNORATES.map((governorate) => (
                      <option key={governorate} value={governorate}>
                        {translateGovernorate(governorate)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3">{t("users.changeStatus")}</h3>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleStatusChange(selectedUser.user_id, "Active")}
                    className={`flex-1 ${
                      selectedUser.status === "Active"
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-gray-300 hover:bg-gray-400"
                    } text-white`}>
                    <Unlock className="w-4 h-4 mr-2" /> {t("users.activate")}
                  </Button>
                  <Button
                    onClick={() => handleStatusChange(selectedUser.user_id, "Deactivated")}
                    className={`flex-1 ${
                      selectedUser.status === "Deactivated"
                        ? "bg-orange-600 hover:bg-orange-700"
                        : "bg-gray-300 hover:bg-gray-400"
                    } text-white`}>
                    <Lock className="w-4 h-4 mr-2" /> {t("users.deactivate")}
                  </Button>
                  <Button
                    onClick={() => handleStatusChange(selectedUser.user_id, "Blocked")}
                    className={`flex-1 ${
                      selectedUser.status === "Blocked"
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-gray-300 hover:bg-gray-400"
                    } text-white`}>
                    <Lock className="w-4 h-4 mr-2" /> {t("users.block")}
                  </Button>
                </div>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium">
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
